import { Grid } from '@material-ui/core';
import React, { useMemo } from 'react';
import { Anchor, Body1, Header1, Step, SubStepDesktop, SubStepMobile, Tag } from './blocks';
import { css } from '@emotion/react';
import { scheduledDatesLabel } from './utils';

const defaultEvent = ({ order, before, after }) => {
    return (
        after || {
            order_id: order.order_id,
            action: `${before?.transition?.next || 'START'}:PICKING_UP`,
        }
    );
};

export default function Scheduled({
    order,
    isDesktop,
    isReceived,
    isCrossdocked,
    isLoading,
    warehouse,
    before,
    after,
    callbacks,
    tz,
    readOnly,
    lastEvent,
}) {
    const pickupDate = after?.est_pickup_date || order?.pickup_date;
    const deliveryDate = after?.est_delivery_date || order?.delivery_date;

    const STEPS = [
        {
            description:
                order.order_type === 'return'
                    ? `Schedule return pickup and ${isCrossdocked ? 'cross-dock' : 'delivery'} dates`
                    : `Schedule ${isReceived ? 'delivery date' : 'load and delivery dates'}`,
            complete: ({ order, before, after }) => {
                return !!after && !!deliveryDate;
            },
            ...(!readOnly
                ? {
                      cta: isReceived ? 'Schedule Date' : 'Schedule Dates',
                      callback: ({ order, before, after }) => {
                          callbacks.editScheduled({
                              event: defaultEvent({ order, before, after }),
                              isReceived,
                              isCrossdocked,
                          });
                      },
                  }
                : {}),
        },
    ];

    const isComplete = useMemo(() => {
        return STEPS.map((step) => step.complete({ order, before, after }));
    });

    const overrideComplete = useMemo(() => {
        return !!after && lastEvent.event_id !== after.event_id;
    }, [before, after, order, lastEvent]);

    const inProgressIdx = useMemo(() => {
        return isComplete.findIndex((x) => !x);
    });

    const allComplete = isComplete.every((x) => x);
    const inProgress = !!before && !(allComplete || overrideComplete);
    const isReceivingOrder = useMemo(() => {
        return !!(order?.wh_events || []).find((e) => e.transition?.next === 'RECEIVING');
    }, [order]);

    return (
        <Step
            isDesktop={isDesktop}
            isComplete={overrideComplete || allComplete}
            inProgress={inProgress}
            label={() => {
                return (
                    <Grid
                        direction="row"
                        container
                        css={css`
                            align-items: center;
                        `}
                    >
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            <Header1>Scheduled Date{isReceived ? '' : 's'}</Header1>
                        </Grid>
                        {!!deliveryDate && (
                            <Grid
                                direction="column"
                                css={css`
                                    margin-right: 8px;
                                `}
                            >
                                <Tag>
                                    <Body1
                                        css={css`
                                            margin-right: 20px;
                                        `}
                                    >
                                        {scheduledDatesLabel({
                                            pickupDate,
                                            deliveryDate,
                                            tz,
                                            isReceived,
                                            hidePickup: isReceivingOrder || !!order?.forward_stocked_status,
                                            isCrossdocked,
                                        })}
                                    </Body1>
                                    {!readOnly && (
                                        <Anchor
                                            onClick={() =>
                                                callbacks.editScheduled({
                                                    event: defaultEvent({ order, before, after }),
                                                    isReceived,
                                                    isCrossdocked,
                                                })
                                            }
                                        >
                                            <Header1>Edit</Header1>
                                        </Anchor>
                                    )}
                                </Tag>
                            </Grid>
                        )}
                    </Grid>
                );
            }}
        >
            {() => (
                <>
                    {STEPS.map(({ description, cta, callback }, idx) => {
                        const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;
                        return (
                            <SubStep
                                key={idx}
                                isComplete={isComplete[idx]}
                                inProgress={inProgressIdx === idx}
                                isLoading={isLoading}
                                isLast={idx === STEPS.length - 1}
                                description={description}
                                cta={cta}
                                secondaryCta={null}
                                callback={callback ? () => callback({ order, before, after }) : null}
                                secondaryCallback={null}
                            />
                        );
                    })}
                </>
            )}
        </Step>
    );
}
